import React, { useState, useRef } from 'react';
import { uploadRiskEvidence } from '../lib/hybridStorageService';

/**
 * Helper แปลงโครงสร้าง image item ให้เป็น Object มาตรฐาน
 * รองรับทั้ง Dual-object record และ Legacy string URL
 */
export const normalizeRiskImage = (img) => {
  if (!img) return null;
  if (typeof img === 'string') {
    return {
      id: img,
      filename: 'evidence.jpg',
      full_url: img,
      thumb_url: img,
      size_full_bytes: 0,
      size_thumb_bytes: 0,
      storage_backend: 'cloud'
    };
  }
  if (typeof img === 'object') {
    const full = img.full_url || img.url || img.thumb_url || '';
    const thumb = img.thumb_url || img.url || img.full_url || '';
    return {
      id: img.id || full || `img_${Date.now()}`,
      filename: img.filename || 'evidence.jpg',
      full_url: full,
      thumb_url: thumb,
      size_full_bytes: img.size_full_bytes || 0,
      size_thumb_bytes: img.size_thumb_bytes || 0,
      mime_type: img.mime_type || 'image/jpeg',
      storage_backend: img.storage_backend || 'hybrid',
      ...img
    };
  }
  return null;
};

/**
 * คอมโพเนนต์อัปโหลดรูปภาพหลักฐานความเสี่ยง (DentalRiskUploadZone)
 * สถาปัตยกรรม Hybrid Storage:
 * - Full-resolution 4K: สำรองภาพต้นฉบับเข้า Synology NAS volume3 (On-Premise Intranet)
 * - Fast Thumbnails: Cloudflare R2 / Supabase Storage / Data URL
 * รองรับถ่ายภาพสดจากกล้องมือถือ, เลือกจากคลังภาพ, Dual-object records และ Lightbox Zoom สลับดู 4K
 */
const DentalRiskUploadZone = ({
  images = [],
  onChange = () => {},
  maxImages = 5,
  disabled = false
}) => {
  const [isUploading, setIsUploading] = useState(false);
  const [uploadStatusText, setUploadStatusText] = useState('');
  const [activeLightboxItem, setActiveLightboxItem] = useState(null);
  const [viewHighRes, setViewHighRes] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');

  const cameraInputRef = useRef(null);
  const galleryInputRef = useRef(null);

  // ปรับโครงสร้างรูปภาพให้พร้อมแสดงผล
  const rawList = Array.isArray(images) ? images : [];
  const normalizedImages = rawList.map(normalizeRiskImage).filter(Boolean);

  const handleFilesSelected = async (fileList) => {
    if (!fileList || fileList.length === 0) return;
    setErrorMsg('');

    const files = Array.from(fileList);
    const remainingSlots = maxImages - normalizedImages.length;

    if (remainingSlots <= 0) {
      setErrorMsg(`สามารถแนบรูปภาพได้สูงสุด ${maxImages} รูป`);
      return;
    }

    const filesToUpload = files.slice(0, remainingSlots);
    setIsUploading(true);
    const newEvidenceObjects = [];

    for (let i = 0; i < filesToUpload.length; i++) {
      const file = filesToUpload[i];
      setUploadStatusText(`กำลังสำรองภาพต้นฉบับเข้า Synology NAS volume3... (${i + 1}/${filesToUpload.length})`);

      try {
        // อัปโหลดผ่าน Hybrid Storage Service (Synology NAS volume3 + Cloud Thumbnail)
        const evidenceRecord = await uploadRiskEvidence(file);
        setUploadStatusText('สร้าง Cloud Thumbnail สำเร็จ');
        newEvidenceObjects.push(evidenceRecord);

        // หน่วงเวลาสั้นๆ เพื่อให้ผู้ใช้เห็นสถานะการทำงาน
        await new Promise((r) => setTimeout(r, 250));
      } catch (err) {
        console.error('[DentalRiskUploadZone] Upload error:', err);
        setErrorMsg(`อัปโหลดล้มเหลว (${file.name || 'ไฟล์'}): ${err.message || 'ไม่สามารถติดต่อ NAS'}`);
      }
    }

    setIsUploading(false);
    setUploadStatusText('');

    if (newEvidenceObjects.length > 0) {
      onChange([...rawList, ...newEvidenceObjects]);
    }
  };

  const handleRemoveImage = (indexToRemove) => {
    const updated = rawList.filter((_, idx) => idx !== indexToRemove);
    onChange(updated);
  };

  return (
    <div style={{ width: '100%' }}>
      {/* Hidden file inputs */}
      <input
        ref={cameraInputRef}
        type="file"
        accept="image/*"
        capture="environment"
        disabled={disabled || isUploading}
        style={{ display: 'none' }}
        onChange={(e) => {
          handleFilesSelected(e.target.files);
          e.target.value = '';
        }}
      />
      <input
        ref={galleryInputRef}
        type="file"
        accept="image/*"
        multiple
        disabled={disabled || isUploading}
        style={{ display: 'none' }}
        onChange={(e) => {
          handleFilesSelected(e.target.files);
          e.target.value = '';
        }}
      />

      {/* Action Buttons Bar */}
      <div
        style={{
          display: 'flex',
          gap: '10px',
          flexWrap: 'wrap',
          alignItems: 'center',
          marginBottom: '10px'
        }}
      >
        <button
          type="button"
          disabled={disabled || isUploading || normalizedImages.length >= maxImages}
          onClick={() => cameraInputRef.current?.click()}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '8px',
            padding: '8px 16px',
            backgroundColor: '#0284c7',
            color: '#ffffff',
            border: 'none',
            borderRadius: '10px',
            fontWeight: '600',
            fontSize: '0.88em',
            cursor: (disabled || isUploading || normalizedImages.length >= maxImages) ? 'not-allowed' : 'pointer',
            opacity: (disabled || isUploading || normalizedImages.length >= maxImages) ? 0.6 : 1,
            boxShadow: '0 2px 4px rgba(2, 132, 199, 0.2)',
            transition: 'all 0.15s ease'
          }}
        >
          <span>📸</span>
          <span>ถ่ายภาพหลักฐาน (กล้อง)</span>
        </button>

        <button
          type="button"
          disabled={disabled || isUploading || normalizedImages.length >= maxImages}
          onClick={() => galleryInputRef.current?.click()}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '8px',
            padding: '8px 16px',
            backgroundColor: '#f1f5f9',
            color: '#334155',
            border: '1.5px solid #cbd5e1',
            borderRadius: '10px',
            fontWeight: '600',
            fontSize: '0.88em',
            cursor: (disabled || isUploading || normalizedImages.length >= maxImages) ? 'not-allowed' : 'pointer',
            opacity: (disabled || isUploading || normalizedImages.length >= maxImages) ? 0.6 : 1,
            transition: 'all 0.15s ease'
          }}
        >
          <span>🖼️</span>
          <span>เลือกจากคลังภาพ ({normalizedImages.length}/{maxImages})</span>
        </button>

        {isUploading && (
          <span style={{ fontSize: '0.84em', color: '#0284c7', fontWeight: '500', display: 'flex', alignItems: 'center', gap: '6px' }}>
            <span
              style={{
                display: 'inline-block',
                width: '14px',
                height: '14px',
                border: '2px solid #0284c7',
                borderTopColor: 'transparent',
                borderRadius: '50%',
                animation: 'spin 0.8s linear infinite'
              }}
            />
            {uploadStatusText || 'กำลังอัปโหลด...'}
          </span>
        )}
      </div>

      {errorMsg && (
        <div style={{ color: '#ef4444', fontSize: '0.82em', marginBottom: '8px', fontWeight: '500' }}>
          ⚠️ {errorMsg}
        </div>
      )}

      {/* Grid Previews */}
      {normalizedImages.length > 0 && (
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(100px, 1fr))',
            gap: '10px',
            marginTop: '8px',
            padding: '10px',
            backgroundColor: '#f8fafc',
            borderRadius: '12px',
            border: '1px dashed #cbd5e1'
          }}
        >
          {normalizedImages.map((imgItem, idx) => {
            const previewUrl = imgItem.thumb_url || imgItem.full_url || '';
            const hasNasFull = !!imgItem.full_url && (imgItem.storage_backend?.includes('synology') || imgItem.full_url.includes('risk-api') || imgItem.full_url.includes('synology'));

            return (
              <div
                key={imgItem.id || idx}
                style={{
                  position: 'relative',
                  aspectRatio: '1 / 1',
                  borderRadius: '10px',
                  overflow: 'hidden',
                  backgroundColor: '#e2e8f0',
                  border: '1px solid #cbd5e1',
                  boxShadow: '0 1px 3px rgba(0,0,0,0.08)'
                }}
              >
                <img
                  src={previewUrl}
                  alt={`หลักฐาน ${idx + 1}`}
                  onClick={() => {
                    setActiveLightboxItem(imgItem);
                    setViewHighRes(false);
                  }}
                  style={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover',
                    cursor: 'zoom-in',
                    transition: 'transform 0.15s'
                  }}
                />

                {!disabled && (
                  <button
                    type="button"
                    title="ลบรูปนี้"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleRemoveImage(idx);
                    }}
                    style={{
                      position: 'absolute',
                      top: '4px',
                      right: '4px',
                      width: '22px',
                      height: '22px',
                      borderRadius: '50%',
                      backgroundColor: 'rgba(239, 68, 68, 0.9)',
                      color: '#ffffff',
                      border: 'none',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      cursor: 'pointer',
                      fontSize: '12px',
                      fontWeight: 'bold',
                      boxShadow: '0 2px 4px rgba(0,0,0,0.3)',
                      lineHeight: '1'
                    }}
                  >
                    ✕
                  </button>
                )}

                {/* Storage tag & Index */}
                <div
                  style={{
                    position: 'absolute',
                    bottom: '4px',
                    left: '4px',
                    right: '4px',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    pointerEvents: 'none'
                  }}
                >
                  <span
                    style={{
                      backgroundColor: 'rgba(15, 23, 42, 0.75)',
                      color: '#ffffff',
                      fontSize: '0.62em',
                      padding: '1px 5px',
                      borderRadius: '4px',
                      fontWeight: '600'
                    }}
                  >
                    #{idx + 1}
                  </span>

                  {hasNasFull && (
                    <span
                      style={{
                        backgroundColor: 'rgba(13, 148, 136, 0.85)',
                        color: '#ffffff',
                        fontSize: '0.58em',
                        padding: '1px 4px',
                        borderRadius: '3px',
                        fontWeight: '700'
                      }}
                      title="มีไฟล์ต้นฉบับความละเอียดสูงบน Synology NAS volume3"
                    >
                      NAS 4K
                    </span>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Modal ซูมดูภาพขนาดใหญ่ (Lightbox Zoom Modal พร้อมสลับดู 4K จาก NAS volume3) */}
      {activeLightboxItem && (
        <div
          onClick={() => {
            setActiveLightboxItem(null);
            setViewHighRes(false);
          }}
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(15, 23, 42, 0.9)',
            backdropFilter: 'blur(6px)',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 99999,
            padding: '16px'
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              position: 'relative',
              maxWidth: '92vw',
              maxHeight: '88vh',
              borderRadius: '14px',
              overflow: 'hidden',
              backgroundColor: '#0f172a',
              boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.7)',
              display: 'flex',
              flexDirection: 'column'
            }}
          >
            {/* Modal Header Bar */}
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                padding: '10px 16px',
                backgroundColor: 'rgba(30, 41, 59, 0.95)',
                borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
                gap: '12px',
                flexWrap: 'wrap'
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <span style={{ color: '#f8fafc', fontSize: '0.88em', fontWeight: '700' }}>
                  {activeLightboxItem.filename || 'หลักฐานภาพถ่าย'}
                </span>
                {activeLightboxItem.size_full_bytes > 0 && (
                  <span style={{ color: '#94a3b8', fontSize: '0.75em' }}>
                    ({(activeLightboxItem.size_full_bytes / (1024 * 1024)).toFixed(2)} MB)
                  </span>
                )}
              </div>

              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                {/* 4K NAS High-Res Switch Button */}
                {activeLightboxItem.full_url && (
                  <button
                    type="button"
                    onClick={() => setViewHighRes(!viewHighRes)}
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '6px',
                      padding: '6px 14px',
                      backgroundColor: viewHighRes ? '#0d9488' : 'rgba(255, 255, 255, 0.12)',
                      color: '#ffffff',
                      border: viewHighRes ? '1.5px solid #14b8a6' : '1px solid rgba(255, 255, 255, 0.25)',
                      borderRadius: '8px',
                      fontSize: '0.82em',
                      fontWeight: '700',
                      cursor: 'pointer',
                      transition: 'all 0.15s ease'
                    }}
                  >
                    <span>{viewHighRes ? '🔄 สลับดู Thumbnail' : '🔍 ดูภาพความละเอียดสูงระดับ 4K (จาก NAS volume3)'}</span>
                  </button>
                )}

                {/* Close Button */}
                <button
                  type="button"
                  onClick={() => {
                    setActiveLightboxItem(null);
                    setViewHighRes(false);
                  }}
                  style={{
                    width: '32px',
                    height: '32px',
                    borderRadius: '50%',
                    backgroundColor: 'rgba(255, 255, 255, 0.15)',
                    color: '#ffffff',
                    border: 'none',
                    fontSize: '16px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    cursor: 'pointer'
                  }}
                >
                  ✕
                </button>
              </div>
            </div>

            {/* Image Display */}
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: '#000000',
                padding: '8px',
                overflow: 'auto',
                maxHeight: 'calc(88vh - 54px)'
              }}
            >
              <img
                src={
                  viewHighRes && activeLightboxItem.full_url
                    ? activeLightboxItem.full_url
                    : (activeLightboxItem.thumb_url || activeLightboxItem.full_url)
                }
                alt="ภาพขยายขนาดเต็ม"
                style={{
                  display: 'block',
                  maxWidth: '90vw',
                  maxHeight: 'calc(85vh - 70px)',
                  objectFit: 'contain'
                }}
              />
            </div>

            {/* Footer Storage Indicator */}
            <div
              style={{
                padding: '6px 16px',
                backgroundColor: 'rgba(15, 23, 42, 0.95)',
                borderTop: '1px solid rgba(255, 255, 255, 0.08)',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                fontSize: '0.75em',
                color: '#94a3b8'
              }}
            >
              <span>
                {viewHighRes
                  ? '⚡ แหล่งข้อมูล: Synology NAS Storage volume3 (On-Premise Full 4K Resolution)'
                  : '⚡ แหล่งข้อมูล: Cloud Thumbnail (Compressed Preview for Rapid Mobile Access)'}
              </span>
              {activeLightboxItem.full_url && (
                <a
                  href={activeLightboxItem.full_url}
                  target="_blank"
                  rel="noreferrer"
                  style={{ color: '#38bdf8', textDecoration: 'none', fontWeight: '600' }}
                >
                  เปิดไฟล์ต้นฉบับ ↗
                </a>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DentalRiskUploadZone;

